import React, { useEffect, useState, useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import { loadStripe } from "@stripe/stripe-js";
import successImg from './green-checkmark.jpg';
import { useCart, usePromoCodeInput, EventContext } from '../CartContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

export default function OrderResults() {
  const { updatePromoCode } = usePromoCodeInput();
  const { SetGlobalCartId } = useCart();
  const [searchParams] = useSearchParams();
  const clientSecret = searchParams.get("payment_intent_client_secret");
  const redirectStatus = searchParams.get("redirect_status");
  const orderSuccess = searchParams.get("orderSuccess");
  const [status, setStatus] = useState("Checking order status...");
  const [outcome, setOutcome] = useState("checking"); // checking | success | failed | unknown | processing | none
  const { globalEventId, settings } = useContext(EventContext);

  useEffect(() => {
    const clearCartData = () => {
      localStorage.removeItem('promoCode');
      localStorage.removeItem('cartID');

      SetGlobalCartId({
        cartID: '',
        cartContents: [],
      });

      updatePromoCode(globalEventId, '');
    };

    const checkPaymentStatus = async () => {
      if (redirectStatus === "succeeded") {
        setStatus("Payment successful!");
        setOutcome("success");
        clearCartData();
      } else if (redirectStatus === "failed") {
        setStatus("⚠ Payment failed. Please try again.");
        setOutcome("failed");
      } else if (clientSecret) {
        try {
          const stripe = await stripePromise;
          const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

          if (error || !paymentIntent) {
            setStatus("⚠ We couldn't confirm your payment. Charges can take a few minutes to appear on your statement.");
            setOutcome("unknown");
            return;
          }

          switch (paymentIntent.status) {
            case "succeeded":
              setStatus("Payment successful!");
              setOutcome("success");
              clearCartData();
              break;
            case "processing":
              setStatus("⏳ Payment is still processing...");
              setOutcome("processing");
              break;
            case "requires_payment_method":
              setStatus("⚠ Payment failed. Please try again.");
              setOutcome("failed");
              break;
            default:
              setStatus("⚠ Something went wrong.");
              setOutcome("unknown");
              break;
          }
        } catch (err) {
          setStatus("⚠ Error retrieving payment status.");
          setOutcome("unknown");
        }
      } else if (orderSuccess) {
        setStatus("Order successful!");
        setOutcome("success");
        clearCartData();
      } else {
        setStatus("⚠ No order found.");
        setOutcome("none");
      }
    };

    checkPaymentStatus();
  }, [clientSecret, redirectStatus, orderSuccess]);

  return (
    <Container className="vh-100">
      <Row className="align-items-center vh-100">
        <Col>
          {outcome === "success" && (
            <Row>
              <Col className="text-center">
                <img src={successImg} alt="Success" style={{ width: "20%" }} />
              </Col>
            </Row>
          )}
          <Row>
            <Col className="text-center">
              <p>
                {outcome === "success" ? (
                  <>
                    Thank You, your order has been received.<br />
                    You will receive a confirmation email with your tickets.
                  </>
                ) : (
                  <>{status}</>
                )}
              </p>
            </Col>
          </Row>
          <Row>
            <Col className="text-center">
              {outcome === "checking" ? null : outcome === "failed" ? (
                <Link to="/checkout">
                  <Button variant="primary">Go Back</Button>
                </Link>
              ) : outcome === "unknown" || outcome === "processing" ? (
                <p className="small">
                  Please check your email (including junk) for your tickets.  Charges can take a few minutes to appear on your statement.<br />
                  If you do not receive an email within 15 minutes, please contact us and we'll confirm your order.
                </p>
              ) : (
                <Link to="/">
                  <Button variant="success">Continue Shopping</Button>
                </Link>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
}